import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { LogOut } from "lucide-react";

const EMERGENCE_LOGO =
  "https://customer-assets.emergentagent.com/job_roleplay-mentor-3/artifacts/lkmqxka2_Emergence%20Logo%20Black%2080x265-%20Original.jpg";

export default function Navbar({ minimal = false }) {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  return (
    <header className="sticky top-0 z-30 border-b border-slate-200 bg-white/95 backdrop-blur-md shadow-sm">
      <div className="max-w-7xl mx-auto px-6 sm:px-10 h-16 flex items-center justify-between gap-6">
        <Link to="/" data-testid="nav-logo" className="flex items-center gap-3 group min-w-0">
          <img
            src={EMERGENCE_LOGO}
            alt="Emergence"
            className="h-7 w-auto object-contain shrink-0"
            data-testid="emergence-logo"
          />
          <span className="hidden sm:block w-px h-6 bg-slate-200" aria-hidden />
          <div className="leading-none min-w-0">
            <div
              className="font-display text-base font-bold text-slate-900 tracking-tight"
              data-testid="app-name"
            >
              Leader<span className="text-[#EA580C]">Sim</span>
            </div>
            <div className="text-[9px] tracking-[0.18em] uppercase text-slate-400 mt-0.5">
              Conversations that change behaviour
            </div>
          </div>
        </Link>
        {!minimal && (
          <nav className="flex items-center gap-1 sm:gap-3 shrink-0">
            {user ? (
              <>
                {(user.role === "admin" || user.role === "facilitator" || user.role === "executive") && (
                  <Link
                    to="/analytics"
                    data-testid="nav-analytics"
                    className="hidden sm:inline text-sm text-[#2563EB] hover:text-[#1D4ED8] transition-colors px-3 py-2 font-medium"
                  >
                    Analytics
                  </Link>
                )}
                {(user.role === "admin" || user.role === "facilitator") && (
                  <Link
                    to="/admin/companies"
                    data-testid="nav-admin"
                    className="hidden sm:inline text-sm text-[#2563EB] hover:text-[#1D4ED8] transition-colors px-3 py-2 font-medium"
                  >
                    {user.role === "admin" ? "Admin" : "My Batches"}
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link
                    to="/admin/facilitators"
                    data-testid="nav-facilitators"
                    className="hidden sm:inline text-sm text-[#2563EB] hover:text-[#1D4ED8] transition-colors px-3 py-2 font-medium"
                  >
                    Facilitators
                  </Link>
                )}
                {user.role === "admin" && (
                  <Link
                    to="/admin/usage-report"
                    data-testid="nav-usage-report"
                    className="hidden sm:inline text-sm text-[#2563EB] hover:text-[#1D4ED8] transition-colors px-3 py-2 font-medium"
                  >
                    Usage Report
                  </Link>
                )}
                <Link
                  to="/scenarios"
                  data-testid="nav-scenarios"
                  className="hidden sm:inline text-sm text-slate-600 hover:text-slate-900 transition-colors px-3 py-2"
                >
                  Practice
                </Link>
                <Link
                  to="/journeys"
                  data-testid="nav-journeys"
                  className="hidden sm:inline text-sm text-slate-600 hover:text-slate-900 transition-colors px-3 py-2"
                >
                  Journeys
                </Link>
                <Link
                  to="/history"
                  data-testid="nav-history"
                  className="hidden sm:inline text-sm text-slate-600 hover:text-slate-900 transition-colors px-3 py-2"
                >
                  My sessions
                </Link>
                <span className="hidden md:inline text-sm text-slate-500 px-2">{user.name}</span>
                <Button
                  data-testid="nav-logout-btn"
                  variant="ghost"
                  onClick={() => {
                    logout();
                    navigate("/");
                  }}
                  className="rounded-xl text-slate-600 hover:bg-slate-100 hover:text-slate-900"
                >
                  <LogOut className="w-4 h-4 mr-1.5" strokeWidth={1.75} />
                  Sign out
                </Button>
              </>
            ) : (
              <Link to="/login" data-testid="nav-login">
                <Button className="rounded-xl bg-[#EA580C] hover:bg-[#C2410C] text-white h-9 px-5 text-sm font-semibold shadow-sm">
                  Sign in
                </Button>
              </Link>
            )}
          </nav>
        )}
      </div>
    </header>
  );
}
